import {action, computed, makeObservable, observable, override, runInAction} from 'mobx';

import {api} from '../../ApiFacade';
import {mainState} from '../../AppMainState';
import {confirmAction, showCommunicationError, showDefaultError, showNotFoundToast, showWarning} from '../../helpers/alerts';
import {showLoaderAsync} from '../../helpers/loaders';
import {FlatDocument, FlatListDto, FlatListOrder, GraphTimeSpan, PageUrlDocument, StructuralDataResponse, UserStatus} from '../../swagger/models';
import {FormSelectOption} from '../form/FormSelect';
import {FlatListState} from './FlatListState';
import {toggleInArrayImmutable} from '../../helpers/helpers';
import {AppRoutes} from '../../routing/AppRoutes';

export enum FlatListGroup {
    starred = 'starred',
    assigned = 'assigned',
}

export const DefaultOrderBy = FlatListOrder.LastSeen;
export const DefaultGraphTimeSpan = GraphTimeSpan.Hour;
export const OrderByField = 'orderBy';
export const GraphTimeSpanField = 'graphTimeSpan';
export const PageField = 'page';

let mouseX: number = 0;
let mouseY: number = 0;

document.onmousemove = function (e) {
    mouseX = e.clientX;
    mouseY = e.clientY;
};


export abstract class FlatListForLogGenericState extends FlatListState {
    @observable
    flat?: FlatDocument;
    @observable
    pageUrls?: Array<PageUrlDocument> | null;
    @observable
    pageUrlsPage: number = 1;
    @observable
    errorDetailsExpanded: boolean = false;
    @observable
    quantityExpanded: boolean = false;
    @observable
    listingsExpanded: boolean = false;
    @observable
    notesExpanded: boolean = false;
    @observable
    route?: AppRoutes;
    private resizeTimeout?: NodeJS.Timeout;
    @observable
    statsLoading: boolean = false;
    @observable
    pageUrlsLoading: boolean = false;
    @observable
    flatDeviceInfoLoading: boolean = false;
    @observable
    deviceInfoDetails: boolean = false;
    @observable
    truncateMessage: boolean = true;
    @observable
    structuralData?: StructuralDataResponse | undefined;
    @observable
    structuralDataParsed: object[] = [];
    @observable
    structuralDataSearchValue = '';
    @observable
    structuralDataSearchArray: string[] = [];
    @observable
    structuralDataCreatedDateFrom: Date | undefined;
    @observable
    structuralDataCreatedDateTo: Date | undefined;
    @observable
    showSearchStructuralPopover: boolean = false;
    structuralSearchPopoverSearchElement: string = '';
    structuralSearchPopoverTopPosition: number = 0;
    structuralSearchPopoverLeftPosition: number = 0;
    @observable
    explanation?: string;


    constructor() {
        super(false, true);
        makeObservable(this);
        this.loadQueryParams();

        window.onresize = () => {
            if (this.listParams.limit !== this.getMaxResults()) {
                if (this.resizeTimeout) {
                    clearTimeout(this.resizeTimeout);
                }
                this.resizeTimeout = setTimeout(() => {
                    this.listParams.limit = this.getMaxResults();
                    if (this.listParams.logId) {
                        this.loadList(false);
                    }
                }, 1500);

            }
        };
    }

    get logId() {
        return this.log?.logId;
    }

    @action
    loadFlat(flat?: FlatListDto) {
        this.truncateMessage = true;
        this.structuralDataSearchArray = [];
        this.structuralDataParsed = [];
        this.structuralDataSearchValue = '';
        this.structuralDataCreatedDateFrom = undefined;
        this.structuralDataCreatedDateTo = undefined;
        this.explanation = undefined;

        if (!flat) {
            return;
        }
        this.currentFlatId = flat.flatId!;
        api()
            .flats.apiFlatsLogIdFlatIdGet({
            flatId: flat.flatId!,
            logId: flat.logId!,
        })
            .then((res) => {
                runInAction(() => {
                    this.flat = res.content;
                    // eslint-disable-next-line no-restricted-globals
                    this.flatLoadUrlHandler(flat);
                });
            })
            .catch((err) => {
                if (err && err.status === 400) {
                    runInAction(() => {
                        showNotFoundToast();
                        this.flat = undefined;
                    });

                } else {
                    showCommunicationError(err);
                }
            });
    }

    abstract flatLoadUrlHandler(flat: FlatListDto): void;

    @action
    previousFlat() {
        if (!this.log) {
            return;
        }
        if (this.currentFlatIndex === 0) {
            if (this.log.page! > 1) {
                this.flat = undefined;
                this.currentFlatId = undefined;
                this.changePage(this.listParams.page - 1, true);
            }
            return;
        }
        this.loadFlat(this.log.items![this.currentFlatIndex - 1]);
    }

    @action
    nextFlat() {
        if (!this.log) {
            return;
        }
        if (this.log && this.currentFlatIndex === this.log.items!.length - 1) {
            if (this.log.page! < this.log.totalPages!) {
                this.flat = undefined;
                this.currentFlatId = undefined;
                this.changePage(this.listParams.page + 1);
            }
            return;
        }
        this.loadFlat(this.log.items![this.currentFlatIndex + 1]);
    }

    @computed
    get currentFlatNumber(): number {
        if (!this.log) {
            return 0;
        }
        return this.currentFlatIndex + 1 + (this.log.page! - 1) * this.listParams.limit!;
    }

    @computed
    get currentFlatIndex(): number {
        return this.log?.items?.findIndex((x) => x.flatId === this.flat?.flatId) || 0;
    }

    @computed
    get previousFlatDisabled(): boolean {
        return this.currentFlatIndex === 0 && this.listParams.page === 1;
    }

    @computed
    get nextFlatDisabled(): boolean {
        if (!this.log || !this.log.total) {
            return true;
        }
        return this.currentFlatNumber === this.log.total;
    }

    @action
    ignoreFlat(e: React.MouseEvent<HTMLButtonElement>) {
        if (e.shiftKey) {
            this.ignoreFlatAction();
        } else {
            confirmAction('Are you sure?', () => {
                this.ignoreFlatAction();
            });
        }
    }

    private ignoreFlatAction() {
        if (!this.flat) {
            return;
        }
        api()
            .flats.apiFlatsIgnoredPut({
            ignoreFlatRequest: {
                flatId: this.flat.flatId,
                logId: this.flat.logId,
                ignore: true,
            },
        })
            .catch((err) => showCommunicationError(err));

        this.removeFlatFromList(this.flat.flatId!);
    }

    @action
    removeFlatFromList(flatId: string | null | undefined) {
        if (!flatId || !this.log) {
            return;
        }

        this.currentFlatId = undefined;
        this.flat = undefined;
        const i = this.log?.items?.findIndex((x) => x.flatId === flatId);
        if (i !== undefined) {
            this.log.items!.splice(i, 1);
            if (this.log && this.log.items!.length > 0) {
                let flat = this.log.items![i];

                if (!flat) {
                    flat = this.log.items![i - 1];
                }

                if (flat) {
                    this.loadFlat(flat);
                }
            } else {
                this.loadList();
            }
        }
    }

    deleteFlat(e: React.MouseEvent<HTMLButtonElement>) {
        if (mainState.isDemoUser) {
            showWarning('Demo mode', 'Cannot delete items in demo mode');
            return;
        }
        if (e.shiftKey) {
            this.deleteFlatAction();
        } else {
            confirmAction('Are you sure?', () => {
                this.deleteFlatAction();
            });
        }
    }

    @action
    private async deleteFlatAction() {
        if (!this.flat) {
            return;
        }
        await api()
            .flats.apiFlatsLogIdFlatIdDelete({
                flatId: this.flat.flatId!,
                logId: this.flat.logId!,
            })
            .catch((err) => showCommunicationError(err));

        //this.removeFlatFromList(this.flat?.flatId!);
        this.currentFlatId = undefined;
        this.flat = undefined;
        await this.loadList();
    }

    @action
    toggleStarred() {
        if (!this.flat) {
            return;
        }

        api()
            .flats.apiFlatsStarredPut({
            starFlatRequest: {
                flatId: this.flat.flatId,
                logId: this.flat.logId!,
                starred: !this.flat.starred,
            },
        })
            .catch((err) => showCommunicationError(err));

        this.flat.starred = !this.flat.starred;
        const inList = this.log?.items?.find((x) => x.flatId === this.flat?.flatId);
        if (inList) {
            inList.starred = !inList.starred;
        }

        this.flat.starred ? mainState.incrementStarred() : mainState.decrementStarred();
        if (this.log?.logId === FlatListGroup.starred) {
            this.removeFlatFromList(this.flat.flatId);
        }
    }

    @action
    assignCurrentFlat(userId: string | null | undefined) {
        if (!this.flat) {
            return;
        }

        this.flat.assignedTo = userId;
        const flatOnList = this.log?.items?.find((x) => x.flatId === this.flat?.flatId);
        if (flatOnList) {
            flatOnList.assignedTo = userId;
        }

        api()
            .flats.apiFlatsAssignPut({
            assignUserToFlatRequest: {
                flatId: this.flat.flatId,
                logId: this.flat.logId,
                userId: userId || undefined,
            },
        })
            .catch((err) => {
                showDefaultError(err);
            });

        this.flat.assignedTo ? mainState.incrementAssignedTo() : mainState.decrementAssignedTo();
    }

    @computed
    get currentProjectUsers(): FormSelectOption[] {
        let users: FormSelectOption[] = [];

        mainState.projects?.forEach((p) => {
            p.logs?.forEach((l) => {
                if (l.logId === this.flat?.logId && p.users) {
                    users = p.users
                        .filter((x) => x.status === UserStatus.Active)
                        .map((u) => {
                            return {
                                key: u.userId!,
                                label: u.name!,
                            };
                        });
                }
            });
        });

        users.unshift({
            label: '',
            key: undefined,
        });

        return users;
    }

    @action
    toggleAdvancedSearch() {
        this.advancedSearchExpanded = !this.advancedSearchExpanded;
    }


    @action
    toggleNotesExpanded() {
        this.notesExpanded = !this.notesExpanded;
    }

    @action
    toggleListingsExpanded() {
        this.listingsExpanded = !this.listingsExpanded;
    }

    @action
    toggleQuantityExpanded() {
        this.quantityExpanded = !this.quantityExpanded;
    }

    @action
    toggleErrorDetailsExpanded() {
        this.errorDetailsExpanded = !this.errorDetailsExpanded;
    }

    saveNotes() {
        if (!this.flat || !this.flat.notes === undefined) {
            return;
        }
        showLoaderAsync(
            api().flats.apiFlatsNotePut({
                flatNoteRequest: {
                    flatId: this.flat.flatId,
                    logId: this.flat.logId,
                    notes: this.flat.notes,
                },
            })
        ).catch((err) => {
            showCommunicationError(err);
        });
    }

    @action
    setNotes(v: string) {
        if (!this.flat || v === undefined) {
            return;
        }
        this.flat.notes = v;
    }

    @override
    setOrderBy(v: FlatListOrder) {
        this.listParams.orderBy = v;
        this.saveQueryParams();
        this.loadList();
    }

    loadFlatDeviceInfo() {
        if (!this.flat || this.flat.deviceInfo) {
            return;
        }

        this.flatDeviceInfoLoading = true;
        api().flats.apiFlatsLogIdFlatIdDeviceinfoGet({
            flatId: this.flat.flatId!,
            logId: this.flat.logId!,
        })
            .then((res) => {
                runInAction(() => {
                    if (this.flat && res.content) {
                        this.flat.deviceInfo = res.content;
                    }
                });
            })
            .catch((err) => showCommunicationError(err))
            .finally(() => runInAction(() => this.flatDeviceInfoLoading = false));
    }

    @action
    loadStatsInfo() {
        if (!this.flat || this.flat.deviceInfo) {
            return;
        }

        this.statsLoading = true;
        api().flats.apiFlatsLogIdFlatIdStatsGet({
            flatId: this.flat.flatId!,
            logId: this.flat.logId!,
        })
            .then((res) => {
                runInAction(() => {
                    if (this.flat && res.content) {
                        this.flat.stats = res.content;
                    }
                });
            })
            .catch((err) => showCommunicationError(err))
            .finally(() => runInAction(() => this.statsLoading = false));
    }

    @action
    loadPageUrlsNextPage() {
        this.pageUrlsPage++;
        this.loadPageUrls();
    }

    @action
    loadPageUrlsPreviousPage() {
        this.pageUrlsPage--;
        this.loadPageUrls();
    }

    @action
    loadPageUrls() {
        if (!this.flat || this.flat.deviceInfo) {
            return;
        }

        this.pageUrlsLoading = true;
        api().flats.apiFlatsLogIdFlatIdPageurlsGet({
            flatId: this.flat.flatId!,
            logId: this.flat.logId!,
            page: this.pageUrlsPage,
        })
            .then((res) => {
                runInAction(() => {
                    this.pageUrls = res.content;
                });
            })
            .catch((err) => showCommunicationError(err))
            .finally(() => runInAction(() => this.pageUrlsLoading = false));
    }

    deleteFlats() {
        if (mainState.isDemoUser) {
            showWarning('Cannot delete elements in demo mode', 'Please create own account. It\'s free');
            return;
        }

        confirmAction('Are you sure? All records will be deleted according to your search criteria', () => {
            api().flats.apiFlatsDelete(this.listParams).then(() => {
                this.loadList();
            }).catch(err => showCommunicationError(err));
        });
    }

    flatClickHandler(v: FlatListDto) {
        this.loadFlat(v);
    }

    @action
    toggleTruncate() {
        this.truncateMessage = !this.truncateMessage;
        if (window.getSelection) {// @ts-ignore
            window.getSelection().removeAllRanges();
        } else { // @ts-ignore
            if (document.selection) {
                // @ts-ignore
                document.selection.empty();
            }
        }
    }

    @action
    toggleDeviceInfoDetails() {
        this.deviceInfoDetails = !this.deviceInfoDetails;
    }

    @action
    loadStructuralData(f: FlatDocument, page: number) {
        this.structuralData = undefined;
        showLoaderAsync(api().flats.apiFlatsLogIdFlatIdStructuraldataGet({
            flatId: f.flatId!,
            logId: f.logId!,
            limit: 1,
            page: page,
            createdFrom: this.structuralDataCreatedDateFrom,
            createdTo: this.structuralDataCreatedDateTo,
            filters: this.structuralDataSearchArray.concat(this.structuralDataSearchValue || []),
        }).then(res => {
            runInAction(() => {
                this.structuralDataParsed = [];
                res.content?.items?.forEach(sItem => {
                    const parsedItem = {};
                    if (!sItem.data) {
                        return;
                    }
                    Object.keys(sItem.data).forEach(strKey => {
                        const last = strKey.split('_').pop();
                        strKey.split('_').reduce(function (r: any, k: any) {
                            if (!r[k]) {
                                r[k] = {};
                            }
                            if (k === last && last !== undefined) {
                                // @ts-ignore
                                r[k] = sItem.data[strKey];
                            }
                            return r[k];
                            // @ts-ignore
                        }, parsedItem);
                    });
                    this.structuralDataParsed.push(parsedItem);
                });

                this.structuralData = res.content;
            });
        }).catch(err => showCommunicationError(err)));
    }

    searchStructuralData(f: FlatDocument, page: number, value: string) {

    }

    @action
    setSearchStructuralDataValue(value: string) {
        this.structuralDataSearchValue = value;
    }

    @action
    toggleStructuralSearchArray(s1: string) {
        this.structuralDataSearchArray = toggleInArrayImmutable(this.structuralDataSearchArray, s1);
        this.loadStructuralData(this.flat!, 1);
    }

    @action
    toggleFlatListWithStructuralData() {
        if (this.listParams.searchStructuralDataFilters) {
            this.listParams.searchStructuralDataFilters = undefined;
        } else {
            this.listParams.searchStructuralDataFilters = this.structuralDataSearchArray;
        }

        this.loadList();
    }

    @action
    setSearchStructuralCreatedDateTo(d: Date | undefined) {
        this.structuralDataCreatedDateFrom = d;
    }

    @action
    setSearchStructuralCreatedDateFrom(d: Date | undefined) {
        this.structuralDataCreatedDateFrom = d;
    }

    @action
    searchStructuralPopoverOpen() {
        this.showSearchStructuralPopover = true;
        this.structuralSearchPopoverTopPosition = mouseY;
        this.structuralSearchPopoverLeftPosition = mouseX;
    }

    @action
    hideSearchStructuralPopover() {
        this.showSearchStructuralPopover = false;
    }

    aiExplain(message: string, language: string) {
        showLoaderAsync(api().explanation.apiExplanationListPost({
            explanationRequest: {
                message,
                language,
            },
        }).then(res => {
            runInAction(() => {
                this.explanation = res.content?.message;
            });
        }).catch(showCommunicationError));
    }

    googleExplain(messageForHelpSearch: string) {

    }
}
